import { InAppState } from "../base/InAppState";
import { share } from "../../core/Share";
import template from "./EventDetailTemplate.html";

import { Gallery } from "../../core/Gallery";
import { DragScroll } from "../../core/DragScroll";
import { PlanState } from "../plan/PlanState";
import { _lcs } from "../../i18n";

/**
 * EventDetailState — a single event (`/events/:id`). A full-bleed sub-page:
 * no bottom nav, back returns to the events list.
 *
 * The event normally comes from the already-loaded feed (core/Events.js). A
 * deep link or a reload lands here without that feed, so the state falls back
 * to fetching the single event from the API before giving up.
 *
 * The host's images (first = header image) are shown as the hero plus a
 * thumbnail rail; both open the fullscreen viewer at the tapped image.
 */
export class EventDetailState extends InAppState
{
    static ROUTE = "/events/:id";
    static ID = "event-detail";

    showsBottomNav() { return false; }

    async enter()
    {
        const id = this.routeParams.getParam( "id" );

        let event = this.app.events.getDetail( id );
        if ( !event )
        {
            event = await this.app.events.fetchDetail( id );
        }
        if ( !event )
        {
            this.app.router.redirect( "/events" );
            return;
        }

        this.event = event;
        this.images = Array.isArray( event.images ) ? event.images : [];

        this.renderScreen( template, { e: event } );
        this.bind();

        // Hero and thumbnails carry data-gallery indices; the viewer picks
        // them up by delegation (see core/Gallery.js).
        this.gallery = Gallery.attach( this, this.images );
        // Drag only: this rail sits in the middle of a page the wheel is
        // needed to scroll.
        DragScroll.attach( this, this.app.view.pageContent.querySelector( '[data-id="gallery"]' ),
            { wheel : false } );
    }

    async exit()
    {
        // The viewer lives in the device frame, not in the screen — the
        // shell's own cleanup would not reach it.
        if ( this.gallery )
        {
            this.gallery.destroy();
            this.gallery = null;
        }
        await super.exit();
    }

    bind()
    {
        const root = this.app.view.pageContent;

        this.addEventListener( root.querySelector( '[data-id="back"]' ), "click", () => this.app.router.redirect( "/events" ) );
        this.addEventListener( root.querySelector( '[data-id="share"]' ), "click", () => this.share() );

        const add = root.querySelector( '[data-id="add"]' );
        if ( add )
        {
            this.addEventListener( add, "click", () => this.addToPlan( add ) );
        }
    }

    /**
     * Put this event in the guest's plan.
     *
     * Stays on the detail page when it worked: the guest is reading about the
     * event and adding it is a side errand, not a reason to lose their place.
     * Planner.addFromDetail navigates by itself in the cases where there is
     * nothing to add to yet (no account, no plan).
     */
    async addToPlan( button )
    {
        button.disabled = true;
        button.setAttribute( "aria-busy", "true" );

        const outcome = await this.app.planner.addFromDetail( "event", this.event.id, {
            // Handed to the planner so the event is added as soon as the guest
            // has made their first plan.
            onNeedsPlan : () => PlanState.requestAdd( "event", this.event.id )
        } );

        // A redirect has already left this screen; re-enabling a button on a
        // disposed state is at best pointless.
        if ( outcome !== "redirected" )
        {
            button.disabled = false;
            button.removeAttribute( "aria-busy" );
        }
    }

    /**
     * Share the event: the native sheet where the browser offers one (mobile,
     * which is where this app lives), otherwise the clipboard.
     */
    async share()
    {
        await share( this.app, { title : this.event?.title || _lcs( "Veranstaltung" ) } );
    }
}
